/**
 * useNodeConnectionValidation Hook
 * Flow Builder node kapcsolatok validálása
 */

import { useCallback } from 'react';
import { Connection, Node, Edge } from 'reactflow';
import { FlowNode, FlowNodeType } from '../types/flowBuilder';
import {
  canConnect,
  isTypeCompatible,
  canBeFirstNode,
  canBeLastNode,
  getNodeMetadata,
  OutputType,
  InputType
} from '../types/nodeConnectionRules';

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
  warning?: string;
}

export const useNodeConnectionValidation = () => {
  /**
   * Ellenőrzi, hogy egy új kapcsolat létrehozható-e
   */
  const validateConnection = useCallback(
    (
      connection: Connection,
      nodes: Node[],
      edges: Edge[]
    ): ValidationResult => {
      const { source, target, sourceHandle, targetHandle } = connection;

      if (!source || !target) {
        return { isValid: false, reason: 'Hiányzó forrás vagy cél node' };
      }

      // Node-ok megkeresése
      const sourceNode = nodes.find((n) => n.id === source) as FlowNode;
      const targetNode = nodes.find((n) => n.id === target) as FlowNode;

      if (!sourceNode || !targetNode) {
        return { isValid: false, reason: 'Node nem található' };
      }

      // Önmagához való kapcsolódás ellenőrzése
      if (source === target) {
        const metadata = getNodeMetadata(sourceNode.type as FlowNodeType);
        if (!metadata?.canConnectToSelf) {
          return { isValid: false, reason: 'A node nem kapcsolódhat önmagához' };
        }
      }

      // Alapvető kapcsolat validáció
      const basicValidation = canConnect(
        sourceNode.type as FlowNodeType,
        targetNode.type as FlowNodeType
      );

      if (!basicValidation.canConnect) {
        return { isValid: false, reason: basicValidation.reason };
      }

      // Max kapcsolatok ellenőrzése
      const sourceMetadata = getNodeMetadata(sourceNode.type as FlowNodeType);
      const targetMetadata = getNodeMetadata(targetNode.type as FlowNodeType);

      if (sourceMetadata && targetMetadata) {
        // Kimeneti kapcsolatok száma
        const sourceOutputCount = edges.filter((e) => e.source === source).length;
        if (sourceOutputCount >= sourceMetadata.maxOutputConnections) {
          return {
            isValid: false,
            reason: `Maximum ${sourceMetadata.maxOutputConnections} kimeneti kapcsolat engedélyezett`
          };
        }

        // Bemeneti kapcsolatok száma
        const targetInputCount = edges.filter((e) => e.target === target).length;
        if (targetInputCount >= targetMetadata.maxInputConnections) {
          return {
            isValid: false,
            reason: `Maximum ${targetMetadata.maxInputConnections} bemeneti kapcsolat engedélyezett`
          };
        }

        // Többszörös kimenet ellenőrzése (condition, switch)
        if (sourceMetadata.requiresMultipleOutputs && sourceOutputCount === 0) {
          return {
            isValid: true,
            warning: 'Ez a node több kimeneti kapcsolatot igényel (pl. true/false ágak)'
          };
        }
      }

      // Típus kompatibilitás ellenőrzése (ha van handle info)
      if (sourceHandle && targetHandle) {
        // TODO: Handle-specifikus típus ellenőrzés
        // Jelenleg csak alapértelmezett típusokat ellenőrzünk
      }

      return { isValid: true };
    },
    []
  );

  /**
   * Ellenőrzi, hogy egy node lehet-e az első node a flow-ban
   */
  const validateFirstNode = useCallback((nodeType: FlowNodeType): ValidationResult => {
    if (!canBeFirstNode(nodeType)) {
      return {
        isValid: false,
        reason: 'Ez a node típus nem lehet a flow első node-ja. Használj trigger node-ot!'
      };
    }
    return { isValid: true };
  }, []);

  /**
   * Ellenőrzi, hogy egy node lehet-e az utolsó node a flow-ban
   */
  const validateLastNode = useCallback((nodeType: FlowNodeType): ValidationResult => {
    if (!canBeLastNode(nodeType)) {
      return {
        isValid: false,
        warning: 'Ez a node típus általában nem befejező node. Fontos, hogy legyen kimeneti kapcsolata.'
      };
    }
    return { isValid: true };
  }, []);

  /**
   * Teljes flow validáció
   */
  const validateFlow = useCallback(
    (nodes: Node[], edges: Edge[]): ValidationResult => {
      if (nodes.length === 0) {
        return { isValid: false, reason: 'A flow üres' };
      }

      // Első node ellenőrzése (nincs bemeneti él)
      const firstNodes = nodes.filter(
        (node) => !edges.some((edge) => edge.target === node.id)
      );

      if (firstNodes.length === 0) {
        return { isValid: false, reason: 'Nincs kezdő node (körkörös kapcsolat?)' };
      }

      if (firstNodes.length > 1) {
        return {
          isValid: false,
          reason: 'Több kezdő node található. Egy flow-nak csak egy trigger node-ja lehet.'
        };
      }

      // Első node típus ellenőrzése
      const firstNode = firstNodes[0] as FlowNode;
      const firstNodeValidation = validateFirstNode(firstNode.type as FlowNodeType);
      if (!firstNodeValidation.isValid) {
        return firstNodeValidation;
      }

      // Utolsó node-ok ellenőrzése (nincs kimeneti él)
      const lastNodes = nodes.filter(
        (node) => !edges.some((edge) => edge.source === node.id)
      );

      if (lastNodes.length === 0) {
        return {
          isValid: false,
          reason: 'Nincs befejező node (körkörös kapcsolat?)'
        };
      }

      // Ellenőrizzük, hogy minden node elérhető-e
      const reachableNodes = new Set<string>();
      const queue = [firstNode.id];

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (reachableNodes.has(currentId)) continue;

        reachableNodes.add(currentId);

        const outgoingEdges = edges.filter((e) => e.source === currentId);
        outgoingEdges.forEach((e) => queue.push(e.target));
      }

      const unreachableNodes = nodes.filter((n) => !reachableNodes.has(n.id));
      if (unreachableNodes.length > 0) {
        return {
          isValid: false,
          reason: `${unreachableNodes.length} node nem érhető el a flow kezdőpontjából`
        };
      }

      return { isValid: true };
    },
    [validateFirstNode]
  );

  /**
   * Kompatibilis node típusok lekérése egy adott node után
   */
  const getCompatibleNodeTypes = useCallback(
    (sourceNodeType: FlowNodeType, existingEdges: Edge[], sourceNodeId: string): FlowNodeType[] => {
      const sourceMetadata = getNodeMetadata(sourceNodeType);
      if (!sourceMetadata) return [];

      // Ellenőrizzük, hogy van-e még szabad kimeneti kapcsolat
      const currentOutputCount = existingEdges.filter((e) => e.source === sourceNodeId).length;
      if (currentOutputCount >= sourceMetadata.maxOutputConnections) {
        return [];
      }

      // Minden node típust ellenőrzünk
      const allNodeTypes: FlowNodeType[] = [
        'trigger', 'webhook_trigger', 'schedule_trigger', 'form_trigger', 'email_trigger', 'manual_trigger',
        'condition', 'switch', 'merge', 'loop', 'wait',
        'action', 'form', 'approval',
        'notification', 'smtp_email', 'telegram_message', 'sms_send', 'seeme_sms_send',
        'crm_action', 'crm_query', 'get_contact_persons', 'missing_items_query',
        'data_transform', 'json_processor', 'csv_parser', 'math_operation', 'date_time', 'random_generator',
        'file_read', 'file_write', 'pdf_generator', 'excel_processor',
        'database_query', 'database_insert', 'database_update', 'database_delete',
        'openai_chat', 'claude_chat', 'ai_text_generator', 'ai_translator',
        'output_display', 'debug_logger', 'error_logger'
      ];

      return allNodeTypes.filter((targetType) => {
        const result = canConnect(sourceNodeType, targetType);
        return result.canConnect;
      });
    },
    []
  );

  /**
   * Node típus információk lekérése (UI-hoz)
   */
  const getNodeTypeInfo = useCallback((nodeType: FlowNodeType) => {
    const metadata = getNodeMetadata(nodeType);
    if (!metadata) return null;

    return {
      role: metadata.role,
      canBeFirst: metadata.canBeFirst,
      canBeLast: metadata.canBeLast,
      acceptedInputs: metadata.acceptedInputs,
      providedOutputs: metadata.providedOutputs,
      maxInputs: metadata.maxInputConnections,
      maxOutputs: metadata.maxOutputConnections,
      requiresMultipleOutputs: metadata.requiresMultipleOutputs
    };
  }, []);

  return {
    validateConnection,
    validateFirstNode,
    validateLastNode,
    validateFlow,
    getCompatibleNodeTypes,
    getNodeTypeInfo,
    isTypeCompatible
  };
};
